/**
 * ChildCurrentRules.js
 * Pure business logic for Child Current Vaccination scenario
 *
 * Phase 3: Age-based eligibility for the Current tab on child beneficiaries.
 *
 * WHY THIS FILE EXISTS:
 * Child vaccination rules are fundamentally different from PW rules.
 * PW rules are time-from-LMC based (calendar logic).
 * Child rules are age-at-administration based (DOB + material age limits).
 *
 * The three gates a dose must pass to be Eligible today:
 *   1. Age gate   - child's age in days is inside [lower_age_limit, upper_age_limit]
 *   2. Sequence gate - previous dose (N-1) was already administered
 *   3. Administered check - dose is not already recorded (shows the date instead)
 *
 * DoseConfig contract returned by getChildCurrentDoseStatus:
 * {
 *   status        : 'administered' | 'Eligible' | 'Upcoming' | 'AgeExpired' | 'Unknown'
 *   enabled       : boolean   -- true only when UI radio button should be interactive
 *   showRadio     : boolean   -- true when the radio button widget should render
 *   date          : string|null -- formatted 'DD/MM/YYYY' when status is 'administered'
 *   reason        : string|null -- human-readable explanation for Upcoming / AgeExpired
 *   ageInDays     : number    -- child's current age in days (useful for debugging)
 * }
 *
 * NOTE: This file is intentionally dependency-free except for dayjs.
 * It does NOT import from pw-current or pw-previous because the domains are
 * completely separate. Shared helpers would need to live in a shared utilities
 * file if that need arises later.
 */

import dayjs from 'dayjs';
import {
  VACCINE_NAMES,
  matchesVaccineIdentity,
  recordMatchesMaterial,
} from '../../vaccinationUtils/vaccineIdentity';

// ============================================================
// SECTION 1: Age calculation
// ============================================================

/**
 * Parse a child's date of birth (DOB) string into a dayjs object.
 *
 * WHY: The API returns DOBs in multiple formats depending on the
 * registration source ("01-Jan-2000", "1-JAN-1990", "2000-01-01").
 * Dayjs is strict by default so we must tell it which formats to try.
 *
 * @param {string} dob - Date of birth string in any supported format
 * @returns {dayjs.Dayjs} - Parsed dayjs object (may be invalid – caller checks)
 */
export const parseDob = (dob) => {
  if (!dob) return dayjs(null); // Returns an invalid dayjs object

  // Detect the "01-Jan-2000" family of formats by looking for month abbreviations.
  // We check for any of the 12 English month abbreviations case-insensitively.
  const hasMonthAbbrev = /jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec/i.test(dob);

  if (hasMonthAbbrev) {
    // Normalize month abbreviations to proper case (dayjs expects "Jan" not "JAN")
    const normalizedDob = dob.replace(/\b(JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)\b/gi, (match) => {
      return match.charAt(0).toUpperCase() + match.slice(1).toLowerCase();
    });

    // Try both zero-padded and non-zero-padded day variants with normalized format
    return dayjs(normalizedDob, ['DD-MMM-YYYY', 'D-MMM-YYYY'], true);
  }
  
  // Explicitly check for DD-MM-YYYY or DD/MM/YYYY numeric formats if customParseFormat is active.
  if (typeof dayjs().format === 'function') { // Check if we can safely just wrap it in arrays
    const numericParsed = dayjs(dob, ['DD-MM-YYYY', 'DD/MM/YYYY', 'YYYY-MM-DD'], true);
    if (numericParsed.isValid()) {
      return numericParsed;
    }
  }

  // Final fallback (may result in invalid depending on browser engine if format is weird)
  return dayjs(dob, ['DD-MM-YYYY', 'YYYY-MM-DD']);
};

/**
 * Calculate a child's age in days from their DOB to today.
 *
 * This is the single canonical age calculation for all child scenarios.
 * Using a dedicated function (rather than inline math) makes it easy to
 * mock in tests and ensures consistent rounding behaviour across the app.
 *
 * @param {string} dob - Date of birth string
 * @returns {number} - Age in whole days (0 when DOB is unparseable)
 */
export const calculateChildAgeInDays = (dob) => {
  try {
    const dobDate = parseDob(dob);

    if (!dobDate.isValid()) {
      console.error('ChildCurrentRules: could not parse DOB:', dob);
      return 0;
    }

    // dayjs.diff truncates towards zero, which is what we want for "age in complete days"
    return dayjs().diff(dobDate, 'day');
  } catch (error) {
    console.error('ChildCurrentRules: error calculating age in days:', error);
    return 0;
  }
};

// ============================================================
// SECTION 2: Individual eligibility checks (pure, no side-effects)
// ============================================================

/**
 * Gate 1: Check whether the child's current age falls inside the dose's
 * allowed age window defined by the material's lower/upper_age_limits arrays.
 *
 * WHY UPPER LIMIT MATTERS:
 * The UIP schedule has hard cutoffs (e.g., OPV is only given below 5 years).
 * A dose whose upper age limit has passed is permanently unavailable –
 * this is a different state from 'Upcoming' (not yet old enough).
 * We surface this as 'AgeExpired' so the UI can give a clearer message.
 *
 * @param {number} childAgeInDays - Pre-calculated child age
 * @param {number} doseIndex      - Zero-based dose index
 * @param {Object} material       - Full material object from all-uip-materials API
 * @returns {{ pass: boolean, state: 'ok'|'TooYoung'|'AgeExpired', reason: string|null }}
 */
export const checkAgeWindow = (childAgeInDays, doseIndex, material) => {
  if (!material) {
    return { pass: true, state: 'ok', reason: null };
  }

  const isPrecaution = typeof material.no_of_doses === 'number' && doseIndex >= material.no_of_doses;
  const pIndex = isPrecaution ? doseIndex - material.no_of_doses : 0;

  const lowerAgeArray = isPrecaution && material.no_of_precaution_doses > 0
    ? material.precaution_lower_age_limits
    : material.lower_age_limits;

  const upperAgeArray = isPrecaution && material.no_of_precaution_doses > 0
    ? material.precaution_upper_age_limits
    : material.upper_age_limits;

  const targetIndex = isPrecaution ? pIndex : doseIndex;

  const lower = lowerAgeArray?.[targetIndex] ?? 0;
  const upper = upperAgeArray?.[targetIndex] ?? 0;

  if (childAgeInDays < lower) {
    const displayLowerArray = isPrecaution && material.no_of_precaution_doses > 0
      ? material.precaution_display_lower_age_limits
      : material.display_lower_age_limits;
    const displayLower = displayLowerArray?.[targetIndex] || `${lower} days`;
    return {
      pass: false,
      state: 'TooYoung',
      reason: null
    };
  }

  if (upper > 0 && childAgeInDays > upper) {
    return {
      pass: false,
      state: 'AgeExpired',
      reason: 'Not Given'
    };
  }

  return { pass: true, state: 'ok', reason: null };
};

/**
 * Gate 2: Verify that dose N-1 has already been administered before
 * allowing dose N. This enforces sequential scheduling (e.g., RVV-2
 * cannot be given before RVV-1).
 *
 * WHY WE USE decryptFunction HERE:
 * The API stores dose numbers as encrypted strings. We must decrypt them
 * to compare numerically. The decrypt function is injected (not imported
 * directly) so this module stays testable without depending on crypto code.
 *
 * @param {number}   doseIndex           - Zero-based dose index to check
 * @param {string}   materialName        - material_name to filter records
 * @param {Array}    administeredDoses   - Vaccination history from the profile API
 * @param {Function} decryptFunction     - Injected decryption function
 * @returns {{ pass: boolean, reason: string|null }}
 */
export const checkSequentialDependency = (
  doseIndex,
  materialName,
  administeredDoses,
  decryptFunction,
  identityContext = null
) => {
  // Dose 0 (first dose) has no predecessor – always allowed
  if (doseIndex === 0) {
    return { pass: true, reason: null };
  }

  // We need to verify dose number (doseIndex) in 1-based terms is (doseIndex + 1),
  // so the PREVIOUS dose number is doseIndex (still 1-based).
  const requiredPreviousDoseNumber = doseIndex; // 1-based: dose index 1 requires dose number 1
  const { registry, matchOptions } = identityContext || {};

  const prevDoseAdministered = (administeredDoses || []).some((record) => {
    if (
      !recordMatchesMaterial(
        record,
        materialName,
        decryptFunction,
        registry,
        matchOptions || {}
      )
    ) {
      return false;
    }

    try {
      const decryptedDoseNumber = parseInt(decryptFunction(record.dose), 10);
      return decryptedDoseNumber === requiredPreviousDoseNumber;
    } catch {
      // If decryption fails for a record, skip it rather than crashing
      return false;
    }
  });

  if (!prevDoseAdministered) {
    // 1-based dose name for the user-visible reason string
    return {
      pass: false,
      reason: "Upcoming"
    };
  }

  return { pass: true, reason: null };
};

/**
 * Look up whether a specific dose has already been administered and
 * return its recorded vaccination_date if so.
 *
 * This check runs BEFORE the eligibility gates because an administered dose
 * should always display its date regardless of age or sequencing state.
 *
 * @param {number}   doseIndex         - Zero-based dose index
 * @param {string}   materialName      - material_name string
 * @param {Array}    administeredDoses - Vaccination history array
 * @param {Function} decryptFunction   - Injected decryption function
 * @returns {{ isAdministered: boolean, vaccinationDate: string|null }}
 */
export const findAdministeredDose = (
  doseIndex,
  materialName,
  administeredDoses,
  decryptFunction,
  identityContext = null
) => {
  // 1-based dose number for comparison with the decrypted value
  const targetDoseNumber = doseIndex + 1;
  const { registry, matchOptions } = identityContext || {};

  const record = (administeredDoses || []).find((rec) => {
    if (
      !recordMatchesMaterial(
        rec,
        materialName,
        decryptFunction,
        registry,
        matchOptions || {}
      )
    ) {
      return false;
    }

    try {
      const decryptedDoseNumber = parseInt(decryptFunction(rec.dose), 10);
      return decryptedDoseNumber === targetDoseNumber;
    } catch {
      return false;
    }
  });

  if (record) {
    return { isAdministered: true, vaccinationDate: record.vaccination_date || null };
  }

  return { isAdministered: false, vaccinationDate: null };
};

// ============================================================
// SECTION 2.5: Additional Rules (Frequency Gap, Conflicts)
// ============================================================

/**
 * PRIVATE helper: Resolve the previous dose's administration date (Real or Assumed).
 * Mirrors the logic in ChildPreviousRules.js for consistency.
 */
const _resolvePreviousDoseDate = (
  doseIndex,
  material,
  childDob,
  administeredDoses,
  decryptFunction,
  identityContext = null
) => {
  const dobDate = parseDob(childDob);
  if (!dobDate.isValid()) return null;

  const calculateAssumedDateForIndex = (idx) => {
    if (idx < 0) return dobDate;

    // 1. Check historical records
    if (administeredDoses && decryptFunction && material?.material_name) {
      const { isAdministered, vaccinationDate } = findAdministeredDose(
        idx,
        material.material_name,
        administeredDoses,
        decryptFunction,
        identityContext
      );

      // If it has a real date, use it
      if (isAdministered && vaccinationDate) {
        const parsed = dayjs(vaccinationDate);
        if (parsed.isValid()) return parsed;
      }

      // 2. If it's administered but MISSING a date, calculate assumed date
      if (isAdministered) {
        const prevAssumedDate = calculateAssumedDateForIndex(idx - 1);
        if (!prevAssumedDate) return null;

        const isPrecaution = typeof material.no_of_doses === 'number' && idx >= material.no_of_doses;
        const pIndex = isPrecaution ? idx - material.no_of_doses : 0;
        
        const freqArray = isPrecaution && material.no_of_precaution_doses > 0
          ? material.precaution_dose_frequencies
          : material.dose_frequencies;
        const lowerAgeArray = isPrecaution && material.no_of_precaution_doses > 0
          ? material.precaution_lower_age_limits
          : material.lower_age_limits;

        const frequency = freqArray?.[isPrecaution ? pIndex : idx] ?? 28;
        const lowerLimit = lowerAgeArray?.[isPrecaution ? pIndex : idx] ?? 0;
        
        const dateByFreq = prevAssumedDate.add(frequency, 'day');
        const dateByAge = dobDate.add(lowerLimit, 'day');
        
        return dateByFreq.isAfter(dateByAge) ? dateByFreq : dateByAge;
      }
    }

    return null;
  };

  return calculateAssumedDateForIndex(doseIndex - 1);
};

export const checkFrequencyGap = (
  doseIndex,
  material,
  childDob,
  administeredDoses,
  decryptFunction,
  identityContext = null
) => {
  if (doseIndex === 0 || !material) {
    return { pass: true, reason: null };
  }

  const isPrecaution = typeof material.no_of_doses === 'number' && doseIndex >= material.no_of_doses;
  const pIndex = isPrecaution ? doseIndex - material.no_of_doses : 0;

  const freqArray = isPrecaution && material.no_of_precaution_doses > 0
    ? material.precaution_dose_frequencies
    : material.dose_frequencies;

  const freq = freqArray?.[isPrecaution ? pIndex : doseIndex] ?? 0;

  if (freq === 0) {
    return { pass: true, reason: null };
  }

  // Use the helper to resolve the previous dose date (Real or Assumed)
  const prevDoseDate = _resolvePreviousDoseDate(
    doseIndex,
    material,
    childDob,
    administeredDoses,
    decryptFunction,
    identityContext
  );

  if (!prevDoseDate) {
    // Missing date edge case: skip gap rule validation (sequence check should handle this)
    return { pass: true, reason: null };
  }

  const gapValidDate = prevDoseDate.add(freq, 'day');
  if (dayjs().isBefore(gapValidDate, 'day')) {
    return {
      pass: false,
      reason: `Upcoming ${gapValidDate.format('MMMM D, YYYY')}`,
    };
  }

  return { pass: true, reason: null };
};

/**
 * MMR is an alternate second measles dose. When MR-1 is administered,
 * MMR must respect the same minimum gap as MR-2 (typically 28 days from MR-1).
 */
export const checkMmrGapFromMr1 = (
  materialName,
  childDob,
  administeredDoses,
  decryptFunction,
  identityContext = null
) => {
  const { registry, matchOptions } = identityContext || {};
  const isMmrMaterial = registry
    ? matchesVaccineIdentity(
        { name: materialName },
        VACCINE_NAMES.MMR,
        registry,
        decryptFunction,
        matchOptions || {}
      )
    : materialName === VACCINE_NAMES.MMR;

  if (!isMmrMaterial) {
    return { pass: true, reason: null };
  }

  const mrMaterial = registry?.getByName?.(VACCINE_NAMES.MR)?.material ?? null;

  const gapDays = mrMaterial?.dose_frequencies?.[1] ?? 28;

  const mr1Admin = findAdministeredDose(
    0,
    VACCINE_NAMES.MR,
    administeredDoses,
    decryptFunction,
    identityContext
  );

  if (!mr1Admin.isAdministered) {
    return { pass: true, reason: null };
  }

  let mr1Date = null;
  if (mr1Admin.vaccinationDate) {
    const parsed = dayjs(mr1Admin.vaccinationDate);
    if (parsed.isValid()) {
      mr1Date = parsed;
    }
  }

  if (!mr1Date && mrMaterial) {
    mr1Date = _resolvePreviousDoseDate(
      1,
      mrMaterial,
      childDob,
      administeredDoses,
      decryptFunction,
      identityContext
    );
  }

  if (!mr1Date) {
    return { pass: true, reason: null };
  }

  const gapValidDate = mr1Date.add(gapDays, 'day');
  if (dayjs().isBefore(gapValidDate, 'day')) {
    return {
      pass: false,
      reason: `Upcoming ${gapValidDate.format('MMMM D, YYYY')}`,
    };
  }

  return { pass: true, reason: null };
};

export const checkConflicts = (
  materialName,
  administeredDoses,
  decryptFunction,
  identityContext = null
) => {
  const adminRecords = administeredDoses || [];
  const { registry, matchOptions } = identityContext || {};

  const isDptPrimaryMaterial = registry
    ? matchesVaccineIdentity(
        { name: materialName },
        VACCINE_NAMES.DPT_PRIMARY,
        registry,
        decryptFunction,
        matchOptions || {}
      )
    : materialName === VACCINE_NAMES.DPT_PRIMARY;

  const isPentavalentMaterial = registry
    ? matchesVaccineIdentity(
        { name: materialName },
        VACCINE_NAMES.PENTAVALENT,
        registry,
        decryptFunction,
        matchOptions || {}
      )
    : materialName?.includes("Pentavalent");

  // ---- Pentavalent ↔ DPT primary mutual exclusion ----
  // If Pentavalent has been administered, block DPT primary (not Booster).
  if (isDptPrimaryMaterial) {
    const hasPenta = adminRecords.some((r) =>
      recordMatchesMaterial(
        r,
        VACCINE_NAMES.PENTAVALENT,
        decryptFunction,
        registry,
        matchOptions || {}
      )
    );
    if (hasPenta) {
      return { pass: false, reason: "Pentavalent given" };
    }
  }

  // If DPT primary has been administered, block Pentavalent.
  if (isPentavalentMaterial) {
    const hasDpt = adminRecords.some((r) =>
      recordMatchesMaterial(
        r,
        VACCINE_NAMES.DPT_PRIMARY,
        decryptFunction,
        registry,
        matchOptions || {}
      )
    );
    if (hasDpt) {
      return { pass: false, reason: "DPT given" };
    }
  }

  return { pass: true, reason: null };
};

// ============================================================
// SECTION 3: Core status function (the main public API of this file)
// ============================================================

/**
 * MAIN ENTRY POINT: Determine the DoseConfig for a single dose in the
 * Child Current vaccination scenario.
 *
 * Call order matters:
 *   1. Administered check (early exit – no further logic needed)
 *   2. Age window check (TooYoung → Upcoming, AgeExpired → AgeExpired)
 *   3. Sequence dependency check (previous dose required → Upcoming)
 *   4. All gates passed → Eligible
 *
 * @param {Object}   vaccine           - Vaccine object { id, name, availableDoses, material }
 * @param {number}   doseIndex         - Zero-based dose index
 * @param {string}   childDob          - Child's date of birth string
 * @param {Array}    administeredDoses - Vaccination history from profile API
 * @param {Function} decryptFunction   - Function to decrypt encrypted dose numbers
 * @returns {Object} DoseConfig - see contract at top of file
 */
export const getChildCurrentDoseStatus = (
  vaccine,
  doseIndex,
  childDob,
  administeredDoses,
  decryptFunction,
  identityContext = null
) => {
  const material = vaccine.material || null;
  const materialName = vaccine.name;
  const childAgeInDays = calculateChildAgeInDays(childDob);

  // ---- Check 0: Conflicts ----
  const conflictCheck = checkConflicts(
    materialName,
    administeredDoses,
    decryptFunction,
    identityContext
  );
  if (!conflictCheck.pass) {
    return {
      status: 'AgeExpired', // Maps to disabled state in UI
      enabled: false,
      showRadio: false,
      date: null,
      reason: conflictCheck.reason,
      ageInDays: childAgeInDays
    };
  }

  // ---- Check 1: Already administered? ----
  // This must come first because an administered dose shows its date even if the
  // child is now outside the age window (age windows apply to future administration).
  const { isAdministered, vaccinationDate } = findAdministeredDose(
    doseIndex,
    materialName,
    administeredDoses,
    decryptFunction,
    identityContext
  );

  if (isAdministered) {
    return {
      status: 'administered',
      enabled: false,
      showRadio: false,
      date: vaccinationDate ? dayjs(vaccinationDate).format('DD/MM/YYYY') : null,
      reason: null,
      ageInDays: childAgeInDays
    };
  }

  // ---- Check: Preceding dose expiration ----
  // If a prior dose was missed (age-expired) without being administered, later
  // doses in the series show 'Upcoming' — only the missed dose itself shows
  // 'Not Given' via its own age-window check below.
  for (let i = 0; i < doseIndex; i++) {
    const prevAdmin = findAdministeredDose(
      i,
      materialName,
      administeredDoses,
      decryptFunction,
      identityContext
    );
    if (!prevAdmin.isAdministered) {
      const prevAgeCheck = checkAgeWindow(childAgeInDays, i, material);
      if (!prevAgeCheck.pass && prevAgeCheck.state === 'AgeExpired') {
        return {
          status: 'Upcoming',
          enabled: false,
          showRadio: false,
          date: null,
          reason: 'Upcoming',
          ageInDays: childAgeInDays
        };
      }
    }
  }

  // ---- Check 2: Age window ----
  const ageCheck = checkAgeWindow(childAgeInDays, doseIndex, material);

  if (!ageCheck.pass) {
    // TooYoung maps to 'Upcoming' (will be eligible in the future)
    // AgeExpired maps to its own state so the UI can show a distinct message
    return {
      status: ageCheck.state === 'TooYoung' ? 'Upcoming' : 'AgeExpired',
      enabled: false,
      showRadio: false,
      date: null,
      reason: ageCheck.reason,
      ageInDays: childAgeInDays
    };
  }

  // ---- Check 3: Sequential dependency ----
  const seqCheck = checkSequentialDependency(
    doseIndex,
    materialName,
    administeredDoses,
    decryptFunction,
    identityContext
  );

  if (!seqCheck.pass) {
    return {
      status: 'Upcoming',
      enabled: false,
      showRadio: false,
      date: null,
      reason: seqCheck.reason,
      ageInDays: childAgeInDays
    };
  }

  // ---- Check 4: Dose Frequency Gap ----
  const gapCheck = checkFrequencyGap(
    doseIndex,
    material,
    childDob,
    administeredDoses,
    decryptFunction,
    identityContext
  );

  if (!gapCheck.pass) {
    return {
      status: 'Upcoming',
      enabled: false,
      showRadio: false,
      date: null,
      reason: gapCheck.reason,
      ageInDays: childAgeInDays
    };
  }

  // ---- Check 5: MR-1 → MMR gap (same rule as MR-2) ----
  const mmrGapCheck = checkMmrGapFromMr1(
    materialName,
    childDob,
    administeredDoses,
    decryptFunction,
    identityContext
  );

  if (!mmrGapCheck.pass) {
    return {
      status: 'Upcoming',
      enabled: false,
      showRadio: false,
      date: null,
      reason: mmrGapCheck.reason,
      ageInDays: childAgeInDays
    };
  }

  // ---- All gates passed: Eligible today ----
  return {
    status: 'Eligible',
    enabled: true,
    showRadio: true,
    date: null,
    reason: null,
    ageInDays: childAgeInDays
  };
};
